import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Box,
  Tooltip,
  TableSortLabel,
  Button,
  Pagination,
  Typography,
  CircularProgress,
  TextField,
  IconButton,
  Dialog,
  DialogContent,
  Card,
  CardContent,
  Grid,
  Divider,
} from "@mui/material";
import Select from "react-select";
import {
  RemoveRedEye,
  Edit,
  Check,
  DeleteOutline,
  FileDownload,
} from "@mui/icons-material/";
import { Loader, PopUpWindow, AddPoolActionForm, ViewPoolActionForm } from "../../components";
import axios from "axios";
import apiUrl from "../api";
import io from "socket.io-client";

// Function to generate a color based on the property name
const getColor = (name) => {
  if (!name) return "#6e6e6e";
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 70%, 40%)`;
  return color;
};

function formatDate(date) {
  if (!date) return "";
  date = new Date(date);  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const PoolActionsTable = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [poolActions, setPoolActions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [poolOptions, setPoolOptions] = useState([]);
  const [selectedPoolAction, setSelectedPoolAction] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Pagination and sorting
  const [totalActions, setTotalActions] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionsPerPage, setActionsPerPage] = useState(props.mini ? 5 : 10);
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState(1); // -1 for descending, 1 for ascending
  
  // Search filters
  const [searchProperty, setSearchProperty] = useState(props.searchProperty || "");
  const [searchDone, setSearchDone] = useState(props.searchDone !== undefined ? props.searchDone : "");
  const [searchStartDate, setSearchStartDate] = useState(props.searchStartDate || "");
  const [searchEndDate, setSearchEndDate] = useState(props.searchEndDate || "");

  const user = JSON.parse(localStorage.getItem("user"));

  // Add state variables for summary data
  const [summaryData, setSummaryData] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  const fetchPoolActions = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${apiUrl}/pool-action?page=${currentPage}&limit=${actionsPerPage}&sortField=${sortField}&sortOrder=${sortOrder === 1 ? "asc" : "desc"}${searchProperty ? `&property=${searchProperty.value}` : ""}${searchDone !== "" ? `&done=${searchDone.value !== undefined ? searchDone.value : searchDone}` : ""}${searchStartDate ? `&startDate=${searchStartDate}` : ""}${searchEndDate ? `&endDate=${searchEndDate}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      setPoolActions(response.data.poolActions);
      setTotalActions(response.data.totalItems);
      setPageCount(Math.ceil(response.data.totalItems / actionsPerPage));
      
      // Check if property-specific search with date range is active
      if (searchProperty && searchStartDate && searchEndDate) {
        fetchSummaryData();
      } else {
        setShowSummary(false);
      }
    } catch (error) {
      console.error("Error fetching pool actions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // New function to fetch summary data
  const fetchSummaryData = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/pool-action/summary?property=${searchProperty.value}&startDate=${searchStartDate}&endDate=${searchEndDate}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      setSummaryData(response.data);
      setShowSummary(true);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setShowSummary(false);
    }
  };

  // Function to handle CSV download
  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/pool-action/export-csv?property=${searchProperty.value}&startDate=${searchStartDate}&endDate=${searchEndDate}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          responseType: 'blob',
        }
      );
      
      // Create a URL for the blob data
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a temporary anchor element and trigger the download
      const link = document.createElement('a');
      const propertyName = getPropertyName(searchProperty.value);
      const fileName = `pool_actions_${propertyName}_${searchStartDate}_to_${searchEndDate}.csv`;
      
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      link.remove();
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

 

  const handleMarkAsDone = async (actionId) => {
    try {
      await axios.patch(
        `${apiUrl}/pool-action/${actionId}/done`,
        { time_done: new Date() },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      // Update the local state to reflect the change
      setPoolActions(poolActions.map(action => 
        action._id === actionId ? { ...action, done: true, time_done: new Date() } : action
      ));
    } catch (error) {
      console.error("Error marking action as done:", error);
    }
  };

  const handleDeleteAction = async () => {
    if (!selectedPoolAction) return;
    
    try {
      await axios.delete(
        `${apiUrl}/pool-action/${selectedPoolAction._id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      // Remove the deleted action from the local state
      setPoolActions(poolActions.filter(action => action._id !== selectedPoolAction._id));
      setDeleteDialogOpen(false);
      setSelectedPoolAction(null);
    } catch (error) {
      console.error("Error deleting pool action:", error);
    }
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      // Toggle sort order if the same field was clicked
      setSortOrder(sortOrder * -1);
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder(1);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchPoolActions();
  };

  const handleClearSearch = () => {
    setSearchProperty("");
    setSearchDone("");
    setSearchStartDate("");
    setSearchEndDate("");
    setSortField("created_at");
    setSortOrder(-1);
    setCurrentPage(1);
    setShowSummary(false);
    fetchPoolActions();
  };

  useEffect(() => {
    fetchPoolActions();
  }, [currentPage, sortField, sortOrder]);

  useEffect(() => {

    const fetchProperties = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/property/getPoolProperties`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        setProperties(response.data.properties);
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };
  
    const fetchPoolOptions = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/pool-options/getPoolOptions`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        setPoolOptions(response.data);
      } catch (error) {
        console.error("Error fetching pool options:", error);
      }
    };
    const fetchData = async () => {
      await fetchProperties();
      await fetchPoolOptions();
      await fetchPoolActions();
    };
    
    fetchData();
   
  }, [
    
  ]);

  useEffect(() => {
    console.log("Pool Properties: ", properties);
    console.log("Pool Options: ", poolOptions);
    console.log("Pool Actions: ", poolActions);
  }, [properties, poolOptions, poolActions]);
  

  useEffect(() => {
    if (props.searchStartDate) {
      setSearchStartDate(props.searchStartDate);
    }
    if (props.searchEndDate) {
      setSearchEndDate(props.searchEndDate);
    }
    if (props.searchDone !== undefined) {
      setSearchDone(props.searchDone);
    }
    if (props.searchProperty) {
      setSearchProperty(props.searchProperty);
    }
  }, [props.searchStartDate, props.searchEndDate, props.searchDone, props.searchProperty]);

  const renderPoolOptionsList = (options) => {
    if (!options || options.length === 0) return "-";
    
    return (
      <Box sx={{ maxHeight: 100, overflowY: 'auto' }}>
        {options.map((option, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{option._id ? poolOptions.find(po => po._id === option._id)?.title || '-' : '-'}</Typography>
            <Typography variant="body2">{option.price}€</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p._id === propertyId);
    return property ? property.name : "Unknown Property";
  };

  const getTotalPrice = (poolAction) => {
    if (!poolAction.pool_options || poolAction.pool_options.length === 0) return 0;
    
    const total = poolAction.pool_options.reduce((sum, option) => {
      // Convert comma decimal separators to dots for parsing
      const processedValue = option.value ? option.value.toString().replace(',', '.') : option.value;
      // Only add to sum if value is numeric
      const value = Number(processedValue);
      return sum + (Number(option.price) * (isNaN(value) ? 0 : value));
    }, 0);
    
    // Format to exactly 2 decimal places
    return total.toFixed(2);
  };

  const handlePoolActionUpdate = (updatedAction) => {
    setPoolActions(prevActions => 
      prevActions.map(action => 
        action._id === updatedAction._id ? updatedAction : action
      )
    );
    
    // Also update selected action if it's the one being viewed
    if (selectedPoolAction && selectedPoolAction._id === updatedAction._id) {
      setSelectedPoolAction(updatedAction);
    }
  };

  // Add socket state
  const [socket, setSocket] = useState(null);
  
  // Connect to socket when component mounts
  useEffect(() => {
    const newSocket = io(apiUrl);
    setSocket(newSocket);
    
    // Clean up on unmount
    return () => {
      newSocket.disconnect();
    };
  }, []);
  
  // Listen for socket events
  useEffect(() => {
    if (!socket) return;
    
    socket.on('poolAction', (data) => {
      if (data.action === 'create') {
        // Check if the action should be included based on filters
        const shouldInclude = checkActionAgainstFilters(data.poolAction);
        if (shouldInclude) {
          setPoolActions(prevActions => {
            const newActions = [data.poolAction, ...prevActions];
            // Keep only the first 'actionsPerPage' items if we're on the first page
            return currentPage === 1 ? 
              newActions.slice(0, actionsPerPage) : 
              prevActions;
          });
          setTotalActions(prev => prev + 1);
        }
      } else if (data.action === 'update') {
        setPoolActions(prevActions => 
          prevActions.map(action => 
            action._id === data.poolAction._id ? data.poolAction : action
          )
        );
        
        // If the updated action is currently selected, update the selection too
        if (selectedPoolAction && selectedPoolAction._id === data.poolAction._id) {
          setSelectedPoolAction(data.poolAction);
        }
        
      } else if (data.action === 'delete') {
        setPoolActions(prevActions => 
          prevActions.filter(action => action._id !== data.poolActionId)
        );
        setTotalActions(prev => Math.max(0, prev - 1));
        
        // If the deleted action is currently selected, close any dialogs
        if (selectedPoolAction && selectedPoolAction._id === data.poolActionId) {
          setViewDialogOpen(false);
          setEditDialogOpen(false);
          setDeleteDialogOpen(false);
          setSelectedPoolAction(null);
        }
      }
    });
    
    return () => {
      socket.off('poolAction');
    };
  }, [socket, currentPage, actionsPerPage, selectedPoolAction]);
  
  // Helper function to check if an action meets the current filter criteria
  const checkActionAgainstFilters = (action) => {
    // Check property filter
    if (searchProperty && action.property !== searchProperty.value) {
      return false;
    }
    
    // Check status filter
    if (searchDone !== "" && action.done !== (searchDone.value !== undefined ? searchDone.value : searchDone)) {
      return false;
    }
    
    // Check date filters
    if (searchStartDate) {
      const actionDate = new Date(action.date).setHours(0, 0, 0, 0);
      const startDate = new Date(searchStartDate).setHours(0, 0, 0, 0);
      if (actionDate < startDate) {
        return false;
      }
    }
    
    if (searchEndDate) {
      const actionDate = new Date(action.date).setHours(0, 0, 0, 0);
      const endDate = new Date(searchEndDate).setHours(0, 0, 0, 0);
      if (actionDate > endDate) {
        return false;
      }
    }
    
    return true;
  };

  // Handle refresh trigger from parent component
  useEffect(() => {
    if (props.refreshTrigger !== undefined) {
      fetchPoolActions();
    }
  }, [props.refreshTrigger]);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {!props.mini && (
            <form 
              onSubmit={handleSearchSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "95%",
                padding: "10px",
              }}
            >
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  label="From date"
                  type="date"
                  value={searchStartDate}
                  onChange={(e) => setSearchStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="To date"
                  type="date"
                  value={searchEndDate}
                  onChange={(e) => setSearchEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
              
              <Box sx={{ display: "flex", gap: 2, mb: 2, zIndex: 1000 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Property</Typography>
                  <Select
                    value={searchProperty}
                    onChange={(selected) => setSearchProperty(selected)}
                    options={properties.map(property => ({
                      value: property._id,
                      label: property.name
                    }))}
                    isClearable
                    placeholder="Select property"
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Status</Typography>
                  <Select
                    value={
                      searchDone === '' 
                        ? null 
                        : { value: searchDone, label: searchDone ? 'Completed' : 'In progress' }
                    }
                    onChange={(selected) => setSearchDone(selected ? selected.value : '')}
                    options={[
                      { value: true, label: 'Completed' },
                      { value: false, label: 'In progress' }
                    ]}
                    isClearable
                    placeholder="Select status"
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    bgcolor: "var(--primary-color)",
                    color: "var(--primary-text-color)",
                    flex: 1
                  }}
                >
                  Search
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleClearSearch}
                  sx={{ flex: 1 }}
                >
                  Clear
                </Button>
              </Box>
            </form>
          )}
          
          {/* Summary Container - Show only when property & date range search is active */}
          {showSummary && summaryData && (
            <Card 
              elevation={3} 
              sx={{ 
                width: '100%', 
                mb: 3, 
                borderRadius: 2,
                background: 'linear-gradient(to right, #f5f7fa, #e3f2fd)'
              }}
            >
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Summary for {getPropertyName(searchProperty.value)} 
                  <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                    ({formatDate(new Date(searchStartDate))} to {formatDate(new Date(searchEndDate))})
                  </Typography>
                </Typography>
                
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Actions:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {summaryData.totalActions}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Completed Actions:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'green' }}>
                      {summaryData.completedActions}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Cost (Completed):
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                      {summaryData.totalCost.toFixed(2)} €
                    </Typography>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    variant="contained" 
                    startIcon={<FileDownload />}
                    onClick={handleDownloadCSV}
                    sx={{
                      bgcolor: 'var(--primary-color)',
                      '&:hover': { bgcolor: 'var(--secondary-color)' },
                    }}
                  >
                    Download CSV
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
          
          <TableContainer component={Paper} sx={{ width: '100%', mt: props.mini ? 1 : 2, maxHeight: props.mini ? 300 : 'none' }}>
            <Table stickyHeader aria-label="pool actions table" size={props.mini ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell>Actions</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "property"}
                      direction={sortField === "property" && sortOrder === 1 ? "asc" : "desc"}
                      onClick={() => handleSortChange("property")}
                    >
                      Property
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "date"}
                      direction={sortField === "date" && sortOrder === 1 ? "asc" : "desc"}
                      onClick={() => handleSortChange("date")}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "done"}
                      direction={sortField === "done" && sortOrder === 1 ? "asc" : "desc"}
                      onClick={() => handleSortChange("done")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Total Price</TableCell>
                  <TableCell align="right">Charge</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {poolActions.length > 0 ? (
                  poolActions.map((action) => (
                    <TableRow 
                      key={action._id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: action.done ? '#f0f7f0' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View">
                            <IconButton 
                              onClick={() => {
                                setSelectedPoolAction(action);
                                setViewDialogOpen(true);
                              }}
                              size="small"
                            >
                              <RemoveRedEye sx={{ color: "var(--primary-color)" }} />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Edit">
                            <IconButton 
                              onClick={() => {
                                setSelectedPoolAction(action);
                                setEditDialogOpen(true);
                              }}
                              size="small"
                              disabled={action.done}
                            >
                              <Edit sx={{ color: action.done ? "grey" : "var(--primary-color)" }} />
                            </IconButton>
                          </Tooltip>
                          
                          {!action.done && (
                            <Tooltip title="Complete">
                              <IconButton 
                                onClick={() => handleMarkAsDone(action._id)}
                                size="small"
                              >
                                <Check sx={{ color: "green" }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Delete">
                            <IconButton 
                              onClick={() => {
                                setSelectedPoolAction(action);
                                setDeleteDialogOpen(true);
                              }}
                              size="small"
                            >
                              <DeleteOutline sx={{ color: "#d32f2f" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              bgcolor: getColor(getPropertyName(action.property)),
                              fontSize: 12
                            }}
                          >
                            {getPropertyName(action.property).charAt(0)}
                          </Avatar>
                          <Typography>{getPropertyName(action.property)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography>{formatDate(action.date)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.time || "-"}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        {action.done ? (
                          <Tooltip title={`Completed: ${new Date(action.time_done).toLocaleString()}`}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'green', 
                                bgcolor: '#e8f5e9', 
                                p: 0.5, 
                                borderRadius: 1,
                                display: 'inline-block'
                              }}
                            >
                              Completed
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'blue', 
                              bgcolor: '#e3f2fd', 
                              p: 0.5, 
                              borderRadius: 1,
                              display: 'inline-block'
                            }}
                          >
                            In Progress
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {getTotalPrice(action)} €
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="primary">
                          {Number(action.charge || 0).toFixed(2)} €
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>
                        No pool actions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {!props.mini && (
            <Box 
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              px={1}
              width="100%"
            >
              <Typography variant="body2">
                Showing {Math.min((currentPage - 1) * actionsPerPage + 1, totalActions)}-
                {Math.min(currentPage * actionsPerPage, totalActions)} of {totalActions} entries
              </Typography>
              
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                size="medium"
              />
            </Box>
          )}

          {/* View Dialog */}
          {viewDialogOpen && selectedPoolAction && (
            <Dialog
              open={viewDialogOpen}
              onClose={() => {
                setViewDialogOpen(false);
                setSelectedPoolAction(null);
              }}
              maxWidth="md"
              fullWidth
            >
              <DialogContent>
                <ViewPoolActionForm
                  poolAction={selectedPoolAction}
                  propertyName={getPropertyName(selectedPoolAction.property)}
                  poolOptions={poolOptions}
                  onClose={() => {
                    setViewDialogOpen(false);
                    setSelectedPoolAction(null);
                  }}
                  onComplete={(actionId) => {
                    handleMarkAsDone(actionId);
                    fetchPoolActions(); // Refresh data after completion
                  }}
                  onUpdate={handlePoolActionUpdate}
                />
              </DialogContent>
            </Dialog>
          )}

          {/* Edit Dialog */}
          {editDialogOpen && selectedPoolAction && (
            <PopUpWindow
              onClose={() => {
                setEditDialogOpen(false);
                setSelectedPoolAction(null);
              }}
              width="600px"
            >
              <AddPoolActionForm 
                setShowAddPoolActionForm={() => {
                  setEditDialogOpen(false);
                  setSelectedPoolAction(null);
                  fetchPoolActions(); // Refresh the data
                }} 
                editMode={true}
                poolActionData={selectedPoolAction}
              />
            </PopUpWindow>
          )}

          {/* Delete Confirmation Dialog */}
          {deleteDialogOpen && selectedPoolAction && (
            <PopUpWindow
              onClose={() => {
                setDeleteDialogOpen(false);
                setSelectedPoolAction(null);
              }}
              width="400px"
            >
              <Typography variant="h6" gutterBottom>
                Delete Pool Action
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                Are you sure you want to delete this pool action?
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setSelectedPoolAction(null);
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteAction}
                >
                  Delete
                </Button>
              </Box>
            </PopUpWindow>
          )}
        </>
      )}
    </>
  );
};

export default PoolActionsTable;
